import * as i0 from '@angular/core';
import { Injectable, inject, InjectionToken, Injector, NgModule, Inject } from '@angular/core';
import * as i1 from '@acorex/core/translation';
import { AXTranslationService } from '@acorex/core/translation';
import { isString, isEmpty, isNull, isUndefined, isNumber, isArray, find, isEqual, merge } from 'lodash-es';

class AXCallbackValidationRule {
    get name() {
        return 'callback';
    }
    async validate(value, options) {
        const validationResult = options?.validate
            ? options?.validate(value, options)
            : {
                result: false,
                message: 'Invalid callback function',
            };
        const createResult = (result) => ({
            rule: this.name,
            result: result.result,
            message: result.message ?? options?.message ?? '',
            value: value,
        });
        if (typeof validationResult === 'boolean') {
            return createResult({
                result: validationResult,
            });
        }
        if (validationResult instanceof Promise) {
            return validationResult.then(createResult);
        }
        return createResult(validationResult);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXCallbackValidationRule, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXCallbackValidationRule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXCallbackValidationRule, decorators: [{
            type: Injectable
        }] });

class AXRegexValidationRule {
    constructor() {
        this.traslationService = inject(AXTranslationService);
    }
    get name() {
        return 'regex';
    }
    async validate(value, options) {
        // Convert string pattern to RegExp if necessary
        const regexPattern = typeof options.pattern === 'string' ? new RegExp(options.pattern) : options.pattern;
        if (!value || (isString(value) && isEmpty(value.trim()))) {
            return {
                rule: this.name,
                result: true,
                message: null,
                value: value,
            };
        }
        else if (!isString(value)) {
            return {
                rule: this.name,
                result: false,
                message: `${value} is not string value.`,
                value: value,
            };
        }
        const isValid = regexPattern.test(value);
        const translatedMessage = await this.traslationService.translateAsync(options?.message ?? `The value does not match the pattern ${regexPattern}.`, { params: { title: options?.['title'] } });
        return {
            rule: this.name,
            result: isValid,
            message: isValid ? null : translatedMessage,
            value: value,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXRegexValidationRule, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXRegexValidationRule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXRegexValidationRule, decorators: [{
            type: Injectable
        }] });

class AXRequiredValidationRule {
    constructor() {
        this.traslationService = inject(AXTranslationService);
    }
    get name() {
        return 'required';
    }
    async validate(value, options) {
        const rules = [
            { test: (val) => isNull(val) || isUndefined(val) },
            { test: (val) => isString(val) && isEmpty(val.trim()) },
            { test: (val) => isNumber(val) && isNaN(val) },
            { test: (val) => isArray(val) && isEmpty(val) },
        ];
        const failedRule = find(rules, (rule) => rule.test(value));
        const isValid = !failedRule;
        const translatedMessage = await this.traslationService.translateAsync('validation.messages.required', {
            params: { title: options?.['title'] },
        });
        const message = failedRule ? options?.message ?? translatedMessage : null;
        return {
            rule: this.name,
            result: isValid,
            message: message,
            value,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXRequiredValidationRule, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXRequiredValidationRule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXRequiredValidationRule, decorators: [{
            type: Injectable
        }] });

// ax-max-length-validation-rule.ts
class AXLengthValidationRule {
    get name() {
        return 'length';
    }
    async validate(value, options) {
        const isValid = value?.length >= options.min && value?.length <= options.max;
        return {
            rule: this.name,
            result: isValid,
            message: isValid ? null : options?.message ?? `The value must be between ${options.min} and ${options.max} chars`,
            value: value,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLengthValidationRule, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLengthValidationRule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLengthValidationRule, decorators: [{
            type: Injectable
        }] });

// ax-max-length-validation-rule.ts
class AXMaxLengthValidationRule {
    get name() {
        return 'maxLength';
    }
    async validate(value, options) {
        const isValid = isEmpty(value) ? true : value?.length <= options.value;
        return {
            rule: this.name,
            result: isValid,
            message: isValid ? '' : options?.message ?? `The value must not exceed ${options.value} characters.`,
            value: value,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXMaxLengthValidationRule, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXMaxLengthValidationRule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXMaxLengthValidationRule, decorators: [{
            type: Injectable
        }] });

class AXMinLengthValidationRule {
    get name() {
        return 'minLength';
    }
    async validate(value, options) {
        const isValid = isEmpty(value) ? true : value?.length >= options.value;
        return {
            rule: this.name,
            result: isValid,
            message: isValid ? '' : options?.message ?? `The value must be at least ${options.value} characters long.`,
            value: value,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXMinLengthValidationRule, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXMinLengthValidationRule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXMinLengthValidationRule, decorators: [{
            type: Injectable
        }] });

class AXBetweenValidationRule {
    get name() {
        return 'between';
    }
    async validate(value, options) {
        let isValid;
        if (options.inclusive) {
            isValid = value >= options.lowerValue && value <= options.upperValue;
        }
        else {
            isValid = value > options.lowerValue && value < options.upperValue;
        }
        const rangeType = options.inclusive ? 'inclusive' : 'exclusive';
        const baseMessage = `The value must be within the ${rangeType} range of ${options.lowerValue} to ${options.upperValue}.`;
        return {
            rule: this.name,
            result: isValid || isEmpty(value),
            message: isValid ? '' : options?.message ?? baseMessage,
            value: value,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXBetweenValidationRule, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXBetweenValidationRule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXBetweenValidationRule, decorators: [{
            type: Injectable
        }] });

// ax-equal-validation-rule.ts
class AXEqualValidationRule {
    get name() {
        return 'equal';
    }
    async validate(value, options) {
        const isValueEqual = isEqual(value, options.value);
        const isValid = options.not ? !isValueEqual : isValueEqual;
        const defaultMessage = `The value must ${options.not ? 'not ' : ''}be equal to ${options.value}.`;
        return {
            rule: this.name,
            result: isValid,
            message: isValid ? '' : options?.message ?? defaultMessage,
            value: value,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXEqualValidationRule, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXEqualValidationRule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXEqualValidationRule, decorators: [{
            type: Injectable
        }] });

// ax-greater-than-validation-rule.ts
class AXGreaterThanValidationRule {
    get name() {
        return 'greaterThan';
    }
    async validate(value, options) {
        const isValid = isNull(value) || isUndefined(value) ? true : options.inclusive ? value >= options.value : value > options.value;
        const baseMessage = `The value must be ${options.inclusive ? 'greater than or equal to' : 'greater than'} ${options.value}.`;
        return {
            rule: this.name,
            result: isValid,
            message: isValid ? '' : options?.message ?? baseMessage,
            value: value,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXGreaterThanValidationRule, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXGreaterThanValidationRule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXGreaterThanValidationRule, decorators: [{
            type: Injectable
        }] });

// ax-less-than-validation-rule.ts
class AXLessThanValidationRule {
    get name() {
        return 'lessThan';
    }
    async validate(value, options) {
        const isValid = isNull(value) || isUndefined(value) ? true : options.inclusive ? value <= options.value : value < options.value;
        const baseMessage = `The value must be ${options.inclusive ? 'less than or equal to' : 'less than'} ${options.value}.`;
        return {
            rule: this.name,
            result: isValid,
            message: isValid ? '' : options?.message ?? baseMessage,
            value: value,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLessThanValidationRule, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLessThanValidationRule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXLessThanValidationRule, decorators: [{
            type: Injectable
        }] });

const AX_VALIDATION_CONFIG = new InjectionToken('AX_VALIDATION_CONFIG', {
    providedIn: 'root',
    factory: () => {
        return AXValidationDefaultConfig;
    },
});
const AXValidationDefaultConfig = {
    rules: {
        email: {
            rule: 'regex',
            options: {
                pattern: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: 'validation.messages.email',
            },
        },
        phone: {
            rule: 'regex',
            options: {
                pattern: /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/,
                message: 'validation.messages.phone',
            },
        },
    },
};
function validationConfig(config = {}) {
    const result = {
        ...AXValidationDefaultConfig,
        ...config,
    };
    return result;
}

class AXValidationRegistryService {
    get customRules() {
        return this.configs.rules;
    }
    constructor(injector) {
        this.plugins = [];
        this.configs = inject(AX_VALIDATION_CONFIG);
        this.injector = injector;
    }
    register(...plugins) {
        plugins.forEach((t) => {
            const childInjector = Injector.create({
                providers: [{ provide: t, useClass: t, deps: [] }],
                parent: this.injector,
            });
            const v = childInjector.get(t);
            if (v && !this.plugins.some((p) => p.name == v.name)) {
                this.plugins.push(v);
            }
        });
    }
    get(ruleName) {
        return this.plugins.find((c) => c.name == ruleName);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXValidationRegistryService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXValidationRegistryService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXValidationRegistryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i0.Injector }] });
class AXValidationService {
    constructor(pluginRegistry, translationService) {
        this.pluginRegistry = pluginRegistry;
        this.translationService = translationService;
    }
    async validate(ruleName, value, options) {
        const customRule = this.pluginRegistry.customRules[ruleName];
        const validator = this.pluginRegistry.get(ruleName);
        if (!validator && !customRule) {
            return {
                rule: ruleName,
                result: false,
                message: await this.translationService.translateAsync('validation.messages.invalid-rull-name', { params: { name: ruleName } }),
                value,
            };
        }
        if (options?.message) {
            options.message = await this.translationService.translateAsync(options.message);
        }
        //
        if (validator)
            return validator.validate(value, options);
        else {
            return this.validate(customRule.rule, value, merge(options, customRule.options));
        }
    }
    ruleFor(value) {
        return new AXValidationRuleChain(this, value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXValidationService, deps: [{ token: AXValidationRegistryService }, { token: i1.AXTranslationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXValidationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXValidationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: AXValidationRegistryService }, { type: i1.AXTranslationService }] });
class AXValidationRuleChain {
    constructor(validationService, initialValue) {
        this.validationService = validationService;
        this.initialValue = initialValue;
        this.rules = [];
        this.validationSummary = { result: true, rules: [] };
    }
    addRule(name, options) {
        this.rules.push({ name, options });
        return this;
    }
    async validate() {
        for (const rule of this.rules) {
            const result = await this.validationService.validate(rule.name, this.initialValue, rule.options);
            this.validationSummary.rules.push(result);
            if (!result.result) {
                this.validationSummary.result = false;
            }
        }
        return this.validationSummary;
    }
}

const BUILT_IN_RULES = [
    AXRequiredValidationRule,
    AXRegexValidationRule,
    AXRequiredValidationRule,
    AXCallbackValidationRule,
    //
    AXMinLengthValidationRule,
    AXMaxLengthValidationRule,
    AXLengthValidationRule,
    //
    AXEqualValidationRule,
    AXLessThanValidationRule,
    AXGreaterThanValidationRule,
    AXBetweenValidationRule,
];
class AXValidationModule {
    static forRoot(configs) {
        return {
            ngModule: AXValidationModule,
            providers: [
                ...(configs?.rules || []),
                {
                    provide: 'AXValidationModuleFactory',
                    useFactory: (pluginRegistry) => () => {
                        pluginRegistry.register(...[...BUILT_IN_RULES, ...(configs?.rules || [])]);
                    },
                    deps: [AXValidationRegistryService],
                    multi: true,
                },
            ],
        };
    }
    static forChild(configs) {
        return {
            ngModule: AXValidationModule,
            providers: [
                ...(configs?.rules || []),
                {
                    provide: 'AXValidationModuleFactory',
                    useFactory: (pluginRegistry) => () => {
                        pluginRegistry.register(...[...BUILT_IN_RULES, ...(configs?.rules || [])]);
                    },
                    deps: [AXValidationRegistryService],
                    multi: true,
                },
            ],
        };
    }
    /**
     * @ignore
     */
    constructor(instances) {
        instances.forEach((f) => {
            f();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXValidationModule, deps: [{ token: 'AXValidationModuleFactory' }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXValidationModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXValidationModule, providers: [...BUILT_IN_RULES] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXValidationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    providers: [...BUILT_IN_RULES],
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['AXValidationModuleFactory']
                }] }] });

class AXValidationRule {
}

/**
 * Generated bundle index. Do not edit.
 */

export { AXBetweenValidationRule, AXCallbackValidationRule, AXEqualValidationRule, AXGreaterThanValidationRule, AXLengthValidationRule, AXLessThanValidationRule, AXMaxLengthValidationRule, AXMinLengthValidationRule, AXRegexValidationRule, AXRequiredValidationRule, AXValidationDefaultConfig, AXValidationModule, AXValidationRegistryService, AXValidationRule, AXValidationService, AX_VALIDATION_CONFIG, validationConfig };
